import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { Eyebrow } from "@/components/common/Section";
import { Ridgeline } from "@/components/common/Ridgeline";
import { useAudienceMode } from "@/hooks/useAudienceMode";

/**
 * /contact — one door for both audiences.
 * We route ~15 CTAs here (Book a showing, Book a scout call, Ask a
 * question, Start prescreen application, Talk to us about your property).
 * A reason picker keeps the form focused: showing / question / own / general.
 *
 * Default reason auto-selects based on where the visitor came from:
 *   • from any renter-side page → "showing"
 *   • from any owner-side page  → "own"
 *
 * NOTE: The property name can be pre-filled via ?property=... in the URL
 * when linked from a specific listing (future enhancement of listing CTAs).
 */

const REASONS = [
  { key: "showing", label: "Book a showing", tenantSide: true },
  { key: "question", label: "Ask a question", tenantSide: true },
  { key: "own", label: "I own a property", tenantSide: false },
  { key: "general", label: "Something else", tenantSide: null },
];

export default function Contact() {
  const audience = useAudienceMode();
  const { search, state } = useLocation();
  const params = new URLSearchParams(search);
  const initialReason =
    params.get("reason") ||
    (audience === "owner" ? "own" : "showing");
  const initialProperty = params.get("property") || (state && state.property) || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    reason: initialReason,
    property: initialProperty,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Scroll to top when arriving from an in-app link
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setError("Your name and email please — the phone is optional.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/contact-inquiries`,
        form,
      );
      setSuccess(true);
    } catch (err) {
      setError("Something went wrong. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  const activeReason =
    REASONS.find((r) => r.key === form.reason) || REASONS[0];

  return (
    <div data-testid="page-contact">
      {/* ============ HERO ============ */}
      <header
        className="relative overflow-hidden"
        style={{ backgroundColor: "#EDE9E0" }}
        data-nav-theme="light"
      >
        <div className="mx-auto max-w-[1440px] px-6 pb-14 pt-[128px] md:px-10 md:pb-20 md:pt-[168px]">
          <div className="grid grid-cols-1 items-end gap-10 md:grid-cols-12">
            <div className="md:col-span-8">
              <Reveal>
                <Eyebrow>Say hello</Eyebrow>
              </Reveal>
              <Reveal delay={0.05}>
                <h1
                  className="font-display mt-7 max-w-[16ch]"
                  style={{
                    fontSize: "clamp(48px, 7.6vw, 108px)",
                    color: "#201E1A",
                    lineHeight: 0.95,
                    letterSpacing: "-0.015em",
                  }}
                  data-testid="contact-title"
                >
                  We&rsquo;ll{" "}
                  <span className="italic" style={{ color: "#C89A4E" }}>
                    pick up.
                  </span>
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p
                  className="body-measure mt-6 text-[17px] md:text-[19px]"
                  style={{ color: "rgba(32,30,26,0.75)" }}
                >
                  A real person, morning or night, from someone who knows the
                  buildings. Fill in the form, or just call — the number below
                  isn&rsquo;t a hotline, it&rsquo;s our office.
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-4">
              <Reveal delay={0.15}>
                <ul
                  className="space-y-5 border-t pt-6"
                  style={{ borderColor: "rgba(32,30,26,0.16)" }}
                  data-testid="contact-info"
                >
                  <InfoRow
                    icon={Phone}
                    label="Call"
                    value={
                      <a href="tel:+15879001234" className="underline-offset-4 hover:underline">
                        (587) 900-1234
                      </a>
                    }
                  />
                  <InfoRow
                    icon={Mail}
                    label="Email"
                    value={
                      <a href="mailto:hello@roamliving.ca" className="underline-offset-4 hover:underline">
                        hello@roamliving.ca
                      </a>
                    }
                  />
                  <InfoRow
                    icon={MapPin}
                    label="Office"
                    value="118 - 11 Ave SE · Calgary"
                  />
                  <InfoRow
                    icon={Clock}
                    label="Hours"
                    value="Every day, 8am – 8pm MT"
                  />
                </ul>
              </Reveal>
            </div>
          </div>
        </div>
        <div className="w-full opacity-70" aria-hidden>
          <Ridgeline stroke="#C89A4E" height={90} />
        </div>
      </header>

      {/* ============ FORM ============ */}
      <section
        style={{ backgroundColor: "#F8F5EF" }}
        data-nav-theme="light"
      >
        <div className="mx-auto max-w-[1440px] px-6 py-20 md:px-10 md:py-28">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-14">
            {/* left column: reason picker + guidance */}
            <div className="md:col-span-4">
              <Reveal>
                <Eyebrow>What&rsquo;s this about?</Eyebrow>
              </Reveal>
              <ul className="mt-6 space-y-2" data-testid="contact-reasons">
                {REASONS.map((r) => {
                  const on = form.reason === r.key;
                  return (
                    <li key={r.key}>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, reason: r.key })}
                        aria-pressed={on}
                        data-testid={`reason-${r.key}`}
                        className="flex w-full items-center justify-between rounded-2xl px-5 py-4 text-left transition-all"
                        style={{
                          backgroundColor: on ? "#24382E" : "transparent",
                          color: on ? "#F8F5EF" : "#24382E",
                          border: `1px solid ${
                            on ? "#24382E" : "rgba(32,30,26,0.16)"
                          }`,
                        }}
                      >
                        <span className="text-[15px]">{r.label}</span>
                        <span
                          className="label-caps text-[10px]"
                          style={{
                            color: on ? "#E3B268" : "rgba(32,30,26,0.4)",
                          }}
                        >
                          {r.tenantSide === true
                            ? "Rent"
                            : r.tenantSide === false
                              ? "Own"
                              : ""}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <Reveal delay={0.1}>
                <p
                  className="mt-8 text-[13px] leading-[1.6]"
                  style={{ color: "rgba(32,30,26,0.6)" }}
                >
                  You&rsquo;ll hear back within one business day — usually
                  within a few hours during office time.
                </p>
              </Reveal>
            </div>

            {/* right column: form */}
            <div className="md:col-span-8">
              {success ? (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="p-10 text-center md:p-14"
                  style={{
                    borderRadius: 24,
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0 30px 60px -30px rgba(20,35,27,0.35)",
                  }}
                  data-testid="contact-success"
                >
                  <div className="mx-auto w-[min(420px,80%)]">
                    <Ridgeline stroke="#C89A4E" height={90} />
                  </div>
                  <p
                    className="label-caps mt-6 text-[11px]"
                    style={{ color: "#C89A4E" }}
                  >
                    Message sent
                  </p>
                  <h3
                    className="font-display mt-4"
                    style={{
                      fontSize: "clamp(28px,3.6vw,44px)",
                      color: "#201E1A",
                      lineHeight: 1.05,
                    }}
                    data-testid="contact-success-title"
                  >
                    We&rsquo;ve got you.
                  </h3>
                  <p
                    className="mx-auto mt-4 max-w-[42ch] text-[15px] leading-[1.6]"
                    style={{ color: "rgba(32,30,26,0.72)" }}
                  >
                    A real person will be in touch within one business day.
                    In the meantime, {activeReason.tenantSide === false
                      ? "our Owners page tells the story."
                      : "our latest homes are waiting on the Residences page."}
                  </p>
                </motion.div>
              ) : (
                <form
                  onSubmit={submit}
                  className="p-8 md:p-10"
                  style={{
                    borderRadius: 24,
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0 30px 60px -30px rgba(20,35,27,0.35)",
                  }}
                  data-testid="contact-form"
                >
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Field label="Your name" required>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={upd("name")}
                        className="c-input"
                        data-testid="contact-name"
                      />
                    </Field>
                    <Field label="Email" required>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={upd("email")}
                        className="c-input"
                        data-testid="contact-email"
                      />
                    </Field>
                    <Field label="Phone">
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={upd("phone")}
                        className="c-input"
                        data-testid="contact-phone"
                      />
                    </Field>
                    {activeReason.tenantSide === true && (
                      <Field label="Which home? (optional)">
                        <input
                          type="text"
                          value={form.property}
                          onChange={upd("property")}
                          className="c-input"
                          placeholder="e.g. The Larchmont #301"
                          data-testid="contact-property"
                        />
                      </Field>
                    )}
                    {activeReason.tenantSide === false && (
                      <Field label="Property address (optional)">
                        <input
                          type="text"
                          value={form.property}
                          onChange={upd("property")}
                          className="c-input"
                          placeholder="e.g. 1024 - 11 Ave NE, Calgary"
                          data-testid="contact-property"
                        />
                      </Field>
                    )}
                  </div>

                  <div className="mt-6">
                    <Field
                      label={
                        activeReason.key === "showing"
                          ? "Preferred day / time (or ask us to pick)"
                          : activeReason.key === "own"
                            ? "Tell us about the property"
                            : "Your message"
                      }
                    >
                      <textarea
                        rows={5}
                        value={form.message}
                        onChange={upd("message")}
                        className="c-input resize-none"
                        placeholder={
                          activeReason.key === "showing"
                            ? "Weekday evenings work best…"
                            : activeReason.key === "own"
                              ? "How many units, current rents, and when you'd like us to have a look."
                              : "The gist…"
                        }
                        data-testid="contact-message"
                      />
                    </Field>
                  </div>

                  {error && (
                    <p
                      className="mt-6 text-[13px]"
                      style={{ color: "#8B1A0F" }}
                      data-testid="contact-error"
                    >
                      {error}
                    </p>
                  )}

                  <div className="mt-8 flex flex-wrap items-center gap-4">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="pill pill-pine text-[15px] disabled:opacity-60"
                      data-testid="contact-submit"
                    >
                      {submitting ? "Sending…" : "Send message"}
                    </button>
                    <p
                      className="text-[12px]"
                      style={{ color: "rgba(32,30,26,0.55)" }}
                    >
                      We reply within one business day.
                    </p>
                  </div>

                  <style>{`
                    .c-input {
                      width: 100%;
                      background: transparent;
                      border: none;
                      border-bottom: 1px solid rgba(32,30,26,0.22);
                      padding: 0.55rem 0;
                      font-size: 15px;
                      color: #201E1A;
                      outline: none;
                      transition: border-color 0.3s;
                    }
                    .c-input:focus { border-color: #24382E; }
                    .c-input::placeholder { color: rgba(32,30,26,0.35); }
                  `}</style>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block">
    <span
      className="label-caps text-[10px]"
      style={{ color: "rgba(32,30,26,0.55)" }}
    >
      {label}
      {required && <span style={{ color: "#C89A4E" }}> *</span>}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);

const InfoRow = ({ icon: Icon, label, value }) => (
  <li className="flex items-start gap-4">
    <span
      className="mt-1 grid h-9 w-9 shrink-0 place-items-center rounded-full"
      style={{
        backgroundColor: "rgba(32,30,26,0.06)",
        color: "#24382E",
      }}
      aria-hidden
    >
      <Icon size={16} strokeWidth={1.6} />
    </span>
    <div>
      <p
        className="label-caps text-[10px]"
        style={{ color: "rgba(32,30,26,0.55)" }}
      >
        {label}
      </p>
      <p
        className="mt-1 text-[16px]"
        style={{ color: "#201E1A" }}
      >
        {value}
      </p>
    </div>
  </li>
);
